import type {
  CeilingFinish,
  Design,
  FloorFinish,
  Furniture,
  RoomFinishSettings,
  RoomSetting,
  Vec2,
  WallFinish,
} from "../types";
import { resolveModel } from "../furniture/resolve";
import {
  ceilingFinish,
  floorFinish,
  isFinishValid,
  lookById,
  wallFinish,
  type FinishSurfaceKind,
  type Look,
} from "./catalog";

/**
 * The finishes a room actually has: what the user chose, valid for the
 * room's setting, over what we'd infer from the room. A room nobody has
 * touched looks exactly like it did before finishes existed.
 */

export const DEFAULT_TRIM = "#f5f3ee";
export const DEFAULT_DOOR = "#f2f0ea";
export const DEFAULT_EXTERIOR = "#ddd7cc";

const KITCHEN_TEMPLATES = new Set(["island", "fridge", "stove"]);

function isVanity(f: Furniture): boolean {
  return /vanity/i.test(f.name);
}

function templateOf(f: Furniture): string | null {
  const model = resolveModel(f);
  return model.kind === "parametric" ? model.template : null;
}

export type RoomKind = "kitchen" | "bath" | "laundry" | "living";

export function roomKind(design: Pick<Design, "room" | "furniture">): RoomKind {
  const name = design.room.name ?? "";
  if (/bath|powder|ensuite|en-suite|washroom|shower/i.test(name)) return "bath";
  if (/kitchen|kitchenette|galley/i.test(name)) return "kitchen";
  if (/laundry|utility|mud ?room/i.test(name)) return "laundry";
  const templates = design.furniture.map(templateOf);
  if (design.furniture.some(isVanity)) return "bath";
  if (
    templates.some((t) => t != null && KITCHEN_TEMPLATES.has(t)) ||
    design.furniture.some((f) => templateOf(f) === "counter" && !isVanity(f) && !/laundry/i.test(f.name))
  ) {
    return "kitchen";
  }
  if (templates.includes("washer") || design.furniture.some((f) => /laundry/i.test(f.name))) return "laundry";
  return "living";
}

/** Angle (radians) of the longest outline wall; planks run along it. */
export function longestWallAngle(points: Vec2[]): number {
  let best = 0;
  let angle = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len > best + 1e-6) {
      best = len;
      angle = Math.atan2(b.y - a.y, b.x - a.x);
    }
  }
  return angle;
}

export interface InferredFinishes {
  floor: FloorFinish;
  walls: WallFinish;
  ceiling: CeilingFinish;
  trimColor: string;
}

/** What a room gets when nobody has chosen: wood for living spaces, tile for wet rooms. */
export function inferredFinishes(design: Pick<Design, "room" | "furniture">, setting: RoomSetting = "indoor"): InferredFinishes {
  if (setting === "outdoor") {
    return {
      floor: { type: "pavers", color: floorFinish("pavers").swatches[0].hex },
      walls: { type: "fence-wood", color: wallFinish("fence-wood").swatches[0].hex },
      ceiling: { type: "open", color: ceilingFinish("open").swatches[0].hex },
      trimColor: DEFAULT_TRIM,
    };
  }
  const kind = roomKind(design);
  const floor: FloorFinish =
    kind === "bath"
      ? { type: "stone", color: floorFinish("stone").swatches[0].hex }
      : kind === "kitchen" || kind === "laundry"
        ? { type: "porcelain", color: floorFinish("porcelain").swatches[0].hex }
        : { type: "oak", color: floorFinish("oak").swatches[0].hex };
  return {
    floor,
    walls: { type: "paint", color: kind === "bath" ? "#eeece8" : "#ece8e1" },
    ceiling: { type: "paint", color: "#f6f5f2" },
    trimColor: DEFAULT_TRIM,
  };
}

const HEX = /^#[0-9a-f]{6}$/i;

function validColor(hex: unknown, fallback: string): string {
  return typeof hex === "string" && HEX.test(hex) ? hex.toLowerCase() : fallback;
}

export interface EffectiveFinishes {
  setting: RoomSetting;
  /** The Look last applied, while nothing has been changed since. */
  look: Look | undefined;
  floor: FloorFinish & { inferred: boolean };
  walls: WallFinish & { inferred: boolean };
  /** Per-wall finishes valid for the setting, by wall key. */
  wallOverrides: Record<string, WallFinish>;
  ceiling: CeilingFinish & { inferred: boolean };
  trimColor: string;
  trimInferred: boolean;
  /** The room carries any chosen finish (first-time vs returning editors). */
  custom: boolean;
}

export function roomSetting(settings: RoomFinishSettings | undefined): RoomSetting {
  return settings?.setting === "outdoor" ? "outdoor" : "indoor";
}

export function effectiveFinishes(design: Pick<Design, "room" | "furniture">): EffectiveFinishes {
  const saved = design.room.finishes;
  const setting = roomSetting(saved);
  const inferred = inferredFinishes(design, setting);
  const pick = <T extends FloorFinish | WallFinish | CeilingFinish>(surface: FinishSurfaceKind, chosen: T | undefined, fallback: T): T & { inferred: boolean } => {
    if (!chosen || !isFinishValid(surface, chosen.type, setting)) return { ...fallback, inferred: true };
    return Object.assign({}, chosen, { color: validColor(chosen.color, fallback.color), inferred: false as boolean });
  };
  const wallOverrides: Record<string, WallFinish> = {};
  for (const [key, f] of Object.entries(saved?.wallOverrides ?? {})) {
    if (f && isFinishValid("walls", f.type, setting)) {
      wallOverrides[key] = { type: f.type, color: validColor(f.color, wallFinish(f.type).swatches[0].hex) };
    }
  }
  const look = lookById(saved?.look);
  return {
    setting,
    look: look && look.setting === setting ? look : undefined,
    floor: pick("floor", saved?.floor, inferred.floor),
    walls: pick("walls", saved?.walls, inferred.walls),
    wallOverrides,
    ceiling: pick("ceiling", saved?.ceiling, inferred.ceiling),
    trimColor: validColor(saved?.trimColor, inferred.trimColor),
    trimInferred: !saved?.trimColor,
    custom: !!saved && Object.keys(saved).some((k) => k !== "setting"),
  };
}

/** One wall's finish: its override, else the room's walls. */
export function wallFinishFor(eff: EffectiveFinishes, key: string): WallFinish & { override: boolean } {
  const o = eff.wallOverrides[key];
  return o ? { ...o, override: true } : { type: eff.walls.type, color: eff.walls.color, override: false };
}
